import { create } from 'zustand';

/*
 * Refusal-announcement store (E01.F03b.R60; AC-8b85b662 frontend half, honours
 * AC-52585c58). Client-only; nothing server-sourced lives here.
 *
 * On an attempted-and-refused act — a request the server refuses (401/403), the
 * "reached another way" path AC-8b85b662/AC-ed53bcf4 cover — the frontend
 * announces the one generic refusal to a screen reader. The single place a
 * refusal is handled is the 401/403 interceptor in app/query-client.ts (AGENTS.md:
 * "No `if (status === 401)` in components. Auth failures are handled once in
 * app/query-client.ts"), which runs OUTSIDE React. So the announcement rides an
 * imperative client-only store the interceptor writes via `announceRefusal`,
 * and a global live-region host (RefusalAnnouncer, mounted once in the shell like
 * Toaster) voices whatever the store holds (OQ-E01-04, Rick Barker 2026-10-05 —
 * the global analogue of F01's role="alert" sign-in refusal, AC-91b2d2d6).
 *
 * A HIDDEN control announces nothing: the courtesy half (ActGate) renders
 * nothing for a withheld act, so there is no attempt, no refusal, and nothing to
 * announce — silence, not a reason.
 */

/*
 * The ONE generic frontend refusal text (AC-52585c58): it tells the person the
 * act was refused and names NONE of a permission, set, role, the
 * is_administrator privilege, an organization, "Kelly", a scope, or any cause.
 * It is the frontend twin of the domain's rbac.GenericAccessRefusal body — one
 * source of truth, identical whatever the cause (conventions, *Constraint
 * violations and error bodies*: one generic text regardless of which check
 * failed).
 */
export const GENERIC_ACT_REFUSAL = 'That action was refused.';

interface RefusalAnnouncementState {
  /** The current refusal announcement, or null when silent. */
  message: string | null;
  /*
   * A monotonic announcement id, bumped on every `announce` (finding 13). A
   * live region only voices a CHANGE in its content; a second identical
   * refusal writes the same `message` string, so without a changing nonce the
   * DOM would not change and a screen reader would announce nothing. The host
   * keys its announced node on the nonce so each `announce` — even a repeat —
   * is a genuine DOM change the live region voices. `clear()` resets it so a
   * fresh cycle starts clean.
   */
  nonce: number;
  /** Announce a refusal to the live region. */
  announce: (message: string) => void;
  /** Clear the live region back to silence. */
  clear: () => void;
}

export const useRefusalAnnouncementStore = create<RefusalAnnouncementState>(
  (set) => ({
    message: null,
    nonce: 0,
    announce: (message) =>
      set((state) => ({ message, nonce: state.nonce + 1 })),
    clear: () => set({ message: null, nonce: 0 }),
  }),
);

/*
 * The imperative seam the interceptor calls from OUTSIDE React (AGENTS.md:
 * "Guards read stores imperatively … beforeLoad runs outside React"). The
 * interceptor cannot use the hook, so it reaches the store through getState()
 * here. Defaults to the one generic text so the single source of truth is never
 * duplicated at the call site.
 */
export function announceRefusal(message: string = GENERIC_ACT_REFUSAL): void {
  useRefusalAnnouncementStore.getState().announce(message);
}
